import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageMeta from './seo/PageMeta';
import JsonLd from './seo/JsonLd';
import {
  LANGUAGE_FLAGS,
  SUPPORTED_LANGUAGE_COUNT,
  SUPPORTED_LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
  languageLandingPath,
} from '../data/supportedLanguages';
import { getSiteOrigin } from '../utils/siteOrigin';
import { languageLandingHreflangAlternates } from '../utils/hreflang';
import { isSafariBrowser, scrollReveal } from '../utils/safariMotion';
import { openAdobeExchange } from '../utils/analytics';
import './Languages.css';

const PAGE_TITLE =
  'Supported Languages | Substyle for Premiere Pro';
const PAGE_DESCRIPTION = `Substyle supports ${SUPPORTED_LANGUAGE_COUNT} languages for animated subtitles in Adobe Premiere Pro, including ${SUPPORTED_LANGUAGE_NAMES}.`;

const Languages: React.FC = () => {
  const safari = isSafariBrowser();
  const origin = getSiteOrigin().replace(/\/$/, '');
  const hreflangAlternates = useMemo(
    () => languageLandingHreflangAlternates(origin),
    [origin]
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    url: `${origin}/languages`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Substyle',
      url: origin,
    },
  };

  return (
    <div className="languages-page">
      <PageMeta
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        canonicalPath="/languages"
        keywords={`Premiere Pro subtitles, ${SUPPORTED_LANGUAGE_NAMES}, multilingual captions, Substyle`}
        htmlLang="en"
        hreflangAlternates={hreflangAlternates}
      />
      <JsonLd id="substyle-languages-jsonld" data={jsonLd} />

      <section className="languages-page-hero">
        <div className="languages-page-container">
          <motion.h1
            className="languages-page-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Supported languages
          </motion.h1>
          <motion.p
            className="languages-page-lead"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Create animated subtitles in Premiere Pro in{' '}
            {SUPPORTED_LANGUAGE_COUNT} languages — no export to another app
            required.
          </motion.p>
        </div>
      </section>

      <section className="languages-page-body">
        <div className="languages-page-container">
          <motion.ul
            className="languages-page-grid"
            transition={{ duration: 0.6 }}
            {...scrollReveal(safari, { opacity: 0, y: 24 }, { opacity: 1, y: 0 }, 'section')}
          >
            {SUPPORTED_LANGUAGES.map((language) => (
              <li key={language.lang} className="languages-page-item">
                <Link
                  to={languageLandingPath(language)}
                  className="languages-page-item-link"
                >
                  <span className="languages-page-flag" aria-hidden>
                    {LANGUAGE_FLAGS[language.lang] ?? ''}
                  </span>
                  <span className="languages-page-name">{language.name}</span>
                </Link>
              </li>
            ))}
          </motion.ul>

          <motion.div
            className="languages-page-cta"
            transition={{ duration: 0.6, delay: 0.1 }}
            {...scrollReveal(safari, { opacity: 0, y: 16 }, { opacity: 1, y: 0 }, 'nested')}
          >
            <button
              type="button"
              className="cta-button primary"
              onClick={() => openAdobeExchange('languages_hub')}
            >
              Install
            </button>
            <Link to="/pricing" className="languages-page-pricing-link">
              View pricing
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Languages;
